import React, { useMemo } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db, { DEXIE_IS_LOADING } from '../lib/db';
import { MediaAttachmentStates } from '../lib/media_attachments_store';
import { useWorkspaceIdScope } from '../lib/workspaceIdScope';
import MediaAttachmentUploadPreview, { clampUploadProgress, getMediaAttachmentFileName } from './MediaAttachmentUploadPreview';

const stateStyles = {
  [MediaAttachmentStates.pending]: 'bg-gray-200 text-gray-700',
  [MediaAttachmentStates.uploading]: 'bg-blue-100 text-blue-700',
  [MediaAttachmentStates.processing]: 'bg-purple-100 text-purple-700',
  [MediaAttachmentStates.retrying]: 'bg-yellow-100 text-yellow-700',
  [MediaAttachmentStates.completed]: 'bg-green-100 text-green-700',
  [MediaAttachmentStates.error]: 'bg-red-100 text-red-700',
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 102.4) / 10} KB`;
  return `${Math.round(bytes / 1024 / 102.4) / 10} MB`;
};

const formatTimestamp = (timestampSeconds) => {
  if (!Number.isFinite(timestampSeconds)) return '—';
  const date = new Date(timestampSeconds * 1000);
  return `${date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })} ${date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
};

const LogViewerMediaAttachmentsTab = () => {
  const workspaceId = useWorkspaceIdScope();
  const mediaAttachments = useLiveQuery(
    async () => {
      if (!workspaceId) return [];
      const rows = await db.media_attachments.where('workspace_id').equals(workspaceId).toArray();
      rows.sort((a, b) => (Number(b.written_at) || 0) - (Number(a.written_at) || 0));
      return rows;
    },
    [workspaceId],
    DEXIE_IS_LOADING,
  );

  const channels = useLiveQuery(
    async () => {
      if (mediaAttachments == DEXIE_IS_LOADING) return [];
      if (!workspaceId) return [];
      const channelIds = mediaAttachments?.map((mediaAttachment) => mediaAttachment.channel_id);
      if (channelIds.length === 0) return [];
      return db.channels
        .where('[workspace_id+id]')
        .anyOf(channelIds.filter((id) => id != null).map((id) => [workspaceId, String(id)]))
        .toArray();
    },
    [mediaAttachments, workspaceId],
    DEXIE_IS_LOADING,
  );

  const groupedMediaAttachments = useMemo(() => {
    const groups = [];
    const indexByKey = new Map();

    if (mediaAttachments == DEXIE_IS_LOADING) return [];

    mediaAttachments.forEach((mediaAttachment) => {
      const key =
        mediaAttachment.session_key ||
        (mediaAttachment.owner_type && mediaAttachment.owner_id ? `${mediaAttachment.owner_type}:${mediaAttachment.owner_id}` : null);
      if (!indexByKey.has(key)) {
        const entry = { key, mediaAttachments: [] };
        groups.push(entry);
        indexByKey.set(key, entry);
      }
      indexByKey.get(key).mediaAttachments.push(mediaAttachment);
    });

    return groups;
  }, [mediaAttachments]);

  if (mediaAttachments === DEXIE_IS_LOADING || channels === DEXIE_IS_LOADING) {
    return <div className="p-4 text-center text-xs text-gray-500">Loading uploads...</div>;
  }

  if (mediaAttachments.length === 0) {
    return <div className="p-4 text-center text-xs text-gray-500">No uploads yet</div>;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-1 overflow-auto">
        <ul className="divide-y divide-gray-200">
          {groupedMediaAttachments.map(({ key: sessionKey, mediaAttachments: sessionMediaAttachments }) => (
            <React.Fragment key={sessionKey || 'default-session'}>
              <li className="bg-gray-50 px-3 py-1 text-3xs tracking-wide text-gray-600 uppercase">{sessionKey}</li>
              {sessionMediaAttachments.map((mediaAttachment) => {
                const fileName = getMediaAttachmentFileName(mediaAttachment);
                const fileSize = mediaAttachment?.metadata?.content_length;
                const progress = clampUploadProgress(
                  mediaAttachment?.upload?.progress ?? (mediaAttachment?.upload?.state === MediaAttachmentStates.completed ? 100 : 0),
                );
                const stateBadgeClass = stateStyles[mediaAttachment?.upload?.state] || 'bg-gray-100 text-gray-600';
                const progressColor =
                  mediaAttachment?.upload?.state === MediaAttachmentStates.error
                    ? 'bg-red-500'
                    : mediaAttachment?.upload?.state === MediaAttachmentStates.completed
                      ? 'bg-green-500'
                      : mediaAttachment?.upload?.state === MediaAttachmentStates.retrying
                        ? 'bg-yellow-500'
                        : mediaAttachment?.upload?.state === MediaAttachmentStates.processing
                          ? 'bg-purple-500'
                          : 'bg-blue-500';

                return (
                  <li key={mediaAttachment.id} className="flex items-start gap-3 px-3 py-2">
                    <MediaAttachmentUploadPreview hideDuration={true} mediaAttachment={mediaAttachment} size="size-11" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="truncate text-xs font-medium text-gray-800" title={fileName}>
                          {fileName}
                          <span className="opacity-50"> {mediaAttachment.attachment_id}</span>
                        </div>
                        <span className={`rounded px-1 py-0.5 text-3xs font-semibold uppercase ${stateBadgeClass}`}>
                          {mediaAttachment.upload?.state || 'unknown'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 text-3xs text-gray-500">
                        {mediaAttachment.channel_id &&
                          channels &&
                          (() => {
                            const channel = channels.find((c) => c.id === mediaAttachment.channel_id);
                            return channel ? <span className="font-bold">#{channel.name}</span> : null;
                          })()}
                        <span>{formatFileSize(fileSize)}</span>
                      </div>
                      <span className="block text-3xs opacity-50">{formatTimestamp(mediaAttachment.written_at)}</span>
                      {mediaAttachment.upload?.state !== MediaAttachmentStates.completed && (
                        <div className="mt-1">
                          <div className="flex items-center justify-between text-3xs text-gray-500">
                            <span>Progress</span>
                            <span>{progress}%</span>
                          </div>
                          <div className="mt-0.5 h-1 rounded bg-gray-200">
                            <div className={`h-full rounded ${progressColor}`} style={{ width: `${progress}%` }} />
                          </div>
                        </div>
                      )}
                      {mediaAttachment.error && <div className="mt-1 text-3xs text-red-600">{mediaAttachment.error}</div>}
                    </div>
                  </li>
                );
              })}
            </React.Fragment>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LogViewerMediaAttachmentsTab;
